import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Spinner from './Spinner';
import colors from '../theme/colors';

export default function HikeCard({ hike, onClick, onFavoriteToggle }) {
  const navigate = useNavigate();
  const { user, isFavorite, getParticipationStatus, toggleFavorite, getOrganizerStats, hasPermission } = useAuth();
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Use enriched hike data from backend, fallback to AuthContext cache
  const isFavorited = hike.is_favorited !== undefined ? hike.is_favorited : isFavorite(hike.id);
  const participationStatus = hike.participation_status !== undefined
    ? (hike.participation_status ? { registered: true, stato: hike.participation_status } : null)
    : (getParticipationStatus(hike.id) ? { registered: true, stato: getParticipationStatus(hike.id) } : null);

  // Organizer stats for hikes the user organizes
  const organizerStats = getOrganizerStats(hike.id);

  // Check if user can access scheda (admin or organizer)
  const canAccessScheda = !hike.is_scheda_hidden ||
    hasPermission('hike:view_hidden:any') ||
    (hike.organizer_ids && user?.id && hike.organizer_ids.includes(user.id));

  const getTagColor = (tag) => {
    const tagColors = colors.tags[tag] || colors.tags.default;
    return `${tagColors.bg} ${tagColors.text}`;
  };

  const capitalizeFirst = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const formatDate = () => {
    const startDate = capitalizeFirst(new Date(hike.data_inizio).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).replace(', ', ' '));

    if (hike.data_fine) {
      const endDate = capitalizeFirst(new Date(hike.data_fine).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).replace(', ', ' '));
      return `${startDate} - ${endDate}`;
    }
    return startDate;
  };

  const handleCardClick = () => {
    // Don't navigate if scheda is hidden and user can't access it
    if (!canAccessScheda) {
      return;
    }

    if (onClick) {
      onClick();
    } else {
      navigate(`/hike/${hike.id}`);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation(); // Prevent card click

    if (!user) {
      navigate('/login');
      return;
    }

    setFavoriteLoading(true);
    try {
      const newFavoriteState = await toggleFavorite(hike.id);

      // Notify parent component to update local state
      if (onFavoriteToggle) {
        onFavoriteToggle(hike.id, newFavoriteState);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className={`bg-white rounded-lg shadow-sm hover:shadow-md overflow-hidden transition ${canAccessScheda ? 'cursor-pointer' : 'cursor-default'} flex flex-col h-full border border-gray-100 ${hike.is_cancelled ? 'opacity-70' : ''}`}
    >
      <div className="relative">
        <img
          src={hike.photos && hike.photos.length > 0
            ? (hike.photos[0].thumbnail_url || hike.photos[0].media_url)
            : hike.url_foto
          }
          alt={hike.titolo}
          className="w-full h-36 object-cover cursor-default"
          loading="lazy"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        />
        {/* Scheda not available banner - bottom of image */}
        {hike.is_scheda_hidden && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent pt-6 pb-2 px-3">
            <p className="text-white font-semibold text-sm">Dettagli in arrivo</p>
          </div>
        )}
        {/* Badges overlay - top left */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          {/* Cancelled badge - highest priority, shown first */}
          {hike.is_cancelled && (
            <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white shadow">
              Annullata
            </span>
          )}
          {/* Archived badge */}
          {hike.is_archived && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white shadow">
              Archiviata
            </span>
          )}
          {/* Registration status badges - show to all users */}
          {(() => {
            // Calculate registration status
            const approvedCount = organizerStats?.approved_count || 0;
            const isCapacityFull = hike.max_partecipanti && approvedCount >= hike.max_partecipanti;
            const isDeadlinePassed = hike.data_termine_iscrizioni && new Date(hike.data_termine_iscrizioni) < new Date();
            const isManuallyClosed = hike.is_registration_closed;
            const isRegistrationNotOpened = hike.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date();

            // Check if registration has not yet opened
            if (isRegistrationNotOpened) {
              return (
                <span className="px-2 py-1 rounded text-xs font-bold bg-amber-500 text-white shadow">
                  Iscrizioni dal {new Date(hike.data_apertura_iscrizioni).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                </span>
              );
            }

            // Priority: capacity full > manually closed/deadline passed
            if (isCapacityFull) {
              return (
                <span className="px-2 py-1 rounded text-xs font-bold bg-green-600 text-white shadow">
                  Posti esauriti
                </span>
              );
            }
            if (isManuallyClosed || isDeadlinePassed) {
              return (
                <span className="px-2 py-1 rounded text-xs font-bold bg-gray-600 text-white shadow">
                  Iscrizioni chiuse
                </span>
              );
            }
            return null;
          })()}
          {/* Organizer stats */}
          {organizerStats && (
            <>
              {/* Pending requests badge */}
              {organizerStats.pending_count > 0 && (
                <span className="px-2 py-1 rounded text-xs font-bold bg-red-600 text-white shadow">
                  {organizerStats.pending_count} da approvare
                </span>
              )}
              {/* Approved/Max badge OR Completo badge */}
              {organizerStats.max_partecipanti && (
                organizerStats.approved_count >= organizerStats.max_partecipanti ? (
                  <span className="px-2 py-1 rounded text-xs font-bold bg-green-600 text-white shadow">
                    Completo
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded text-xs font-bold bg-blue-600 text-white shadow">
                    {organizerStats.approved_count}/{organizerStats.max_partecipanti} iscritti
                  </span>
                )
              )}
            </>
          )}
        </div>
        {user && (
          <button
            onClick={handleFavoriteClick}
            disabled={favoriteLoading}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 hover:bg-white shadow transition disabled:opacity-50 flex items-center justify-center"
            title={isFavorited ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          >
            {favoriteLoading ? (
              <Spinner size="sm" color="blue" />
            ) : (
              <svg
                className="w-5 h-5 transition"
                fill={isFavorited ? "#FCD34D" : "none"}
                stroke={isFavorited ? "#FCD34D" : "#6B7280"}
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                />
              </svg>
            )}
          </button>
        )}
      </div>
      <div className="p-3 flex flex-col flex-grow">
        {/* Date */}
        <p className="text-sm font-medium text-gray-700 mb-1">
          {formatDate()}
        </p>

        <h3 className="text-base font-semibold text-gray-800 mb-1 line-clamp-2">{hike.titolo}</h3>

        {/* Organizers */}
        {hike.organizers && hike.organizers.length > 0 && (
          <p className="text-xs text-gray-500 mb-2">
            {hike.organizers.map(org => org.display_name).join(', ')}
          </p>
        )}

         {/* Status badges row */}
         <div className="flex flex-wrap gap-1 mb-2">
           {/* Hidden Badge */}
           {hike.is_hidden && (
             <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
               Nascosta
             </span>
           )}
 
           {/* Registration Status Badge */}
           {participationStatus?.registered && (
             participationStatus.stato === 'approvata' ? (
               <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                 Iscritto
               </span>
             ) : participationStatus.stato === 'in_attesa' ? (
               <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                 In Attesa
               </span>
             ) : (
               <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                 Non Approvata
               </span>
             )
           )}
 
           {/* Tags */}
           {hike.tags && hike.tags.map((tag, index) => (
             <span
               key={index}
               className={`px-1.5 py-0.5 rounded text-xs font-medium ${getTagColor(tag)}`}
             >
               {tag}
             </span>
           ))}
         </div>
 
         <p className="text-xs text-gray-500 mb-1">{hike.luogo}</p>
        <p className="text-sm text-gray-600 mb-2 line-clamp-2 flex-grow">{hike.descrizione_breve}</p>

        {/* Metadata */}
        {(hike.metadata?.distanza || hike.metadata?.dislivello || hike.max_partecipanti) && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-2">
            {hike.metadata?.distanza && (
              <div>
                <span className="text-gray-400">Distanza</span>
                <p className="text-gray-600 font-medium">{hike.metadata.distanza}</p>
              </div>
            )}
            {hike.metadata?.dislivello && (
              <div>
                <span className="text-gray-400">Dislivello</span>
                <p className="text-gray-600 font-medium">{hike.metadata.dislivello}</p>
              </div>
            )}
            {hike.max_partecipanti && (
              <div>
                <span className="text-gray-400">👥 Max</span>
                <p className="text-gray-600 font-medium">{hike.max_partecipanti} persone</p>
              </div>
            )}
          </div>
        )}

          {/* Categories - compact */}
          {hike.categories && hike.categories.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-auto pt-2 border-t border-gray-100">
              {hike.categories.slice(0, 4).map((category, index) => (
                <span
                  key={index}
                  className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600"
                >
                  {category}
                </span>
              ))}
              {hike.categories.length > 4 && (
                <span
                  className="px-1.5 py-0.5 rounded text-xs bg-gray-200 text-gray-500"
                  title={hike.categories.slice(4).join(', ')}
                >
                  +{hike.categories.length - 4}
                </span>
              )}
            </div>
          )}
          
          {/* Iscriviti button - only show when hike is open for registration */}
          {!participationStatus?.registered && !hike.is_cancelled && (
            (() => {
              const approvedCount = organizerStats?.approved_count || 0;
              const isCapacityFull = hike.max_partecipanti && approvedCount >= hike.max_partecipanti;
              const isDeadlinePassed = hike.data_termine_iscrizioni && new Date(hike.data_termine_iscrizioni) < new Date();
              const isManuallyClosed = hike.is_registration_closed;
              const isRegistrationNotOpened = hike.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date();

              // Show disabled button if registration not yet opened
              if (isRegistrationNotOpened) {
                return (
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="w-full mt-2 py-2 rounded text-sm font-semibold text-white bg-gray-400 cursor-not-allowed"
                    disabled
                  >
                    Iscrizioni dal {new Date(hike.data_apertura_iscrizioni).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                  </button>
                );
              }

              // Only show button if registration is open
              if (!isCapacityFull && !isManuallyClosed && !isDeadlinePassed) {
                return (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click
                      if (canAccessScheda) {
                        navigate(`/hike/${hike.id}`);
                      }
                    }}
                    className="w-full mt-2 py-2 rounded text-sm font-semibold transition text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Iscriviti
                  </button>
                );
              }
              return null;
            })()
          )}
        </div>
      </div>
    );
}